import Tooltip from 'primevue/tooltip';
import primevueMethods from './primevue-methods';
import generateRandomId from '../../utils/randomNumber';
import { safariAgent } from '../../utils/detectBrowser';

const SHIFT = 12;

export const helpers = {
  alignArrow: function alignArrow(el, arrowEl, tooltipLeft, defaultPosition) {
    const elRect = el.getBoundingClientRect();
    let left;
    if (defaultPosition) {
      left = elRect.left - tooltipLeft - 8;
    } else {
      left = elRect.left - tooltipLeft + Math.floor(el.offsetWidth / 2);
    }
    arrowEl.style.left = `${left}px`;
  },
  alignLeft: function alignLeft(el, tooltipEl) {
    const arrowEl = tooltipEl.querySelector('.p-tooltip-arrow');
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const documentWidth = document.documentElement.clientWidth;

    let left;
    if (tooltipRect.left < 0) {
      left = SHIFT;
    } else if (tooltipRect.right >= documentWidth) {
      left = documentWidth - tooltipEl.offsetWidth - SHIFT;
    }
    tooltipEl.style.left = `${left}px`;

    this.alignArrow(el, arrowEl, left);
  },

  // when width of a tooltip content is set explicitly e.g min-width: 12rem
  isTooltipOverlapBody: function isTooltipOverlapBody(tooltipElelement) {
    return tooltipElelement.getBoundingClientRect().right > document.documentElement.clientWidth;
  },
  getPosition: function getPosition(modifiers) {
    return Object.keys(modifiers).filter((mod) => mod !== 'focus');
  },
};
function handleClick(e) {
  e.target.focus();
}
const AtomicTooltip = {
  created(el, binding, vnode, prevVnode) {
    if (Tooltip.created) {
      Tooltip.created(el, binding, vnode, prevVnode);
    }
  },
  beforeMount(el, binding, vnode, prevVnode) {
    if (Tooltip.beforeMount) {
      binding.modifiers = { ...binding.modifiers };
      binding.modifiers.focus = true;
      if (helpers.getPosition(binding.modifiers).length <= 0) {
        binding.modifiers.right = true; // explicitly define default position
      }
      Tooltip.beforeMount(el, binding, vnode, prevVnode);
    }
  },
  mounted(el, binding, vnode, prevVnode) {
    import('./tooltip.scss');
    if (Tooltip.mounted) {
      Tooltip.mounted(el, binding, vnode, prevVnode);
    }
    let tooltipOffset = null;

    el._mc_focus = function mcFocus() {
      setTimeout(() => {
        const topOrBottom = binding.modifiers.top || binding.modifiers.bottom;
        const tooltipEl = primevueMethods.getTooltipElement(el);

        if (!tooltipEl) {
          return;
        }

        const id = `tooltiptext${generateRandomId()}`;
        el.setAttribute('aria-describedby', id);
        tooltipEl.querySelector('.p-tooltip-text')?.setAttribute('id', id);

        if (vnode) {
          this.scrollCallback(); // alligning the tooltip in a center
        }
        const _isOutOfBounds = primevueMethods.isOutOfBounds(el);
        if (topOrBottom && (_isOutOfBounds || helpers.isTooltipOverlapBody(tooltipEl))) {
          helpers.alignLeft(el, tooltipEl);

          if (primevueMethods.isOutOfBounds(el)) {
            if (binding.modifiers.bottom) {
              primevueMethods.alignTop(el);
            } else if (binding.modifiers.top) {
              primevueMethods.alignBottom(el);
            }
            helpers.alignLeft(el, tooltipEl);
          } else if (helpers.isTooltipOverlapBody(tooltipEl)) {
            helpers.alignLeft(el, tooltipEl);
          }
        }
      }, el.$_ptooltipShowDelay + 20);
    };
    el._mc_close = function mcClose() {
      tooltipOffset = null;
    };
    el.scrollCallback = function scrollCallback() {
      const tooltipElement = primevueMethods.getTooltipElement(el);
      if (!tooltipElement) {
        tooltipOffset = null;
        return;
      }

      function calculateOffset() {
        if (tooltipElement.classList.contains('p-tooltip-bottom')) {
          return 2 * SHIFT;
        }
        if (tooltipElement.classList.contains('p-tooltip-top')) {
          return -tooltipElement.clientHeight;
        }
        return -(tooltipElement.clientHeight / 2) + SHIFT; // right & left modifiers
      }

      tooltipOffset = tooltipOffset === null ? calculateOffset() : tooltipOffset; // cache offset
      const { top } = vnode.el.getBoundingClientRect();
      tooltipElement.style.top = `${top + window.scrollY + tooltipOffset}px`;
      const adaptedPosition = !tooltipElement.classList.contains(`p-tooltip-${helpers.getPosition(binding.modifiers)[0]}`);
      const arrowEl = tooltipElement.querySelector('.p-tooltip-arrow');
      const topOrBottom = binding.modifiers.top || binding.modifiers.bottom;

      if (adaptedPosition || topOrBottom) {
        // recaclulate the arrow position
        const isTopAlignment = tooltipElement.classList.contains('p-tooltip-top');
        const isBottomAlignment = tooltipElement.classList.contains('p-tooltip-bottom');
        const defaultAdaptation = binding.modifiers.right && adaptedPosition && !topOrBottom && !isTopAlignment && !isBottomAlignment;
        helpers.alignArrow(el, arrowEl, tooltipElement.getBoundingClientRect().left, defaultAdaptation);
      }
    };
    el.addEventListener('focus', el._mc_focus);
    el.addEventListener('blur', el._mc_close);

    if (safariAgent) {
      el.addEventListener('click', handleClick);
    }
    document.addEventListener('scroll', el.scrollCallback);
  },
  beforeUpdate(el, binding, vnode, prevVnode) {
    if (Tooltip.beforeUpdate) {
      Tooltip.beforeUpdate(el, binding, vnode, prevVnode);
    }
  },

  updated(el, binding, vnode, prevVnode) {
    if (Tooltip.updated) {
      binding.modifiers = { ...binding.modifiers };
      binding.modifiers.focus = true;
      const position = Object.keys(binding.modifiers).filter((mod) => mod !== 'focus');
      if (position.length <= 0) {
        binding.modifiers.right = true; // explicitly define default position
      }
      Tooltip.updated(el, binding, vnode, prevVnode);
    }
  },
  beforeUnmount(el, binding, vnode, prevVnode) {
    if (Tooltip.beforeUnmount) {
      Tooltip.beforeUnmount(el, binding, vnode, prevVnode);
    }
  },
  unmounted(el, binding, vnode, prevVnode) {
    if (Tooltip.unmounted) {
      Tooltip.unmounted(el, binding, vnode, prevVnode);
    }

    el.removeEventListener('focus', el._mc_focus);
    el.removeEventListener('blur', el._mc_close);
    document.removeEventListener('scroll', el.scrollCallback);
    if (safariAgent) {
      el.removeEventListener('click', handleClick);
    }
  },
};

export default AtomicTooltip;
